import { useCallback, useState } from "react";
import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import type { RootState } from "@/shared/lib/store/store";
import {
  DebouncedMutationOptions,
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";

/**
 * CacheUpdate is a function updating the RKT Query cache with the passed
 * value. The implementation will typically look like this:
 *
 * @example
 * ```
 * const updateEntityCache = (entity: EntityType) =>
 *   entityApi.util.updateQueryData(
 *     "showEntity", // the name of the query endpoint to fetch the entity
 *     id: entity.id,
 *     () => entity,
 *   );
 * ```
 */
type CacheUpdate<T> = (
  value: T,
) => ThunkAction<unknown, RootState, unknown, UnknownAction>;

/**
 * The cache-value argument of the returned setter. Optional when the request
 * *is* a full cache value (PUT: `Req` assignable to `CacheValue`); required when
 * it isn't (PATCH), so a partial request can never be mistaken for the optimistic
 * cache value. The `[ ]` wrap keeps the check non-distributive over unions.
 */
type CacheArg<Req, CacheValue> = [Req] extends [CacheValue]
  ? [cacheValue?: CacheValue]
  : [cacheValue: CacheValue];

/**
 * useOptimisticDebounceMutation returns a `set(request, cache)` function that:
 *
 *  1. immediately patches the RTK Query cache via `updateCache` for instant UI
 *  2. schedules a debounced mutation through {@link useDebouncedMutation}
 *
 * @experimental - This is not really trivial, usage must be tested at
 * selected sites and see if it leads to issues in real-life usage,
 * before applying it globally (as of 07.2026).
 *
 * Using useOptimisticDebounceMutation allows you to use the RTK
 * Query cache state to render your UI state and get immediate
 * updates on value change. This eliminates the need to intoduce a
 * local `const [value, setValue] = useState()` to control input
 * fields. This guarantees that any cache invalidation will
 * always appear in the UI. - This guarantee can be tricky to
 * keep with local state in combination with debounced input.
 * NOTE: There is one gotcha: Get the RTK query state directly
 * from the hook returning the value and do not pass it down
 * the component tree. The latter may cause a re-render and
 * movement of the cursor to the end of an input field.
 *
 * When using useOptimisticDebounceMutation, you MUST make sure that
 * the mutation called will reset the cache value on error. This keeps
 * the UI state in-sync with the server state. Otherwise, invalid
 * values may linger in the cache. The simple solution for this is
 * to invalidate the tag and re-fetch the server state. See RTK
 * Query documentation (read "Tip" section for debounce == "triggered
 * in short succession"):
 * https://redux-toolkit.js.org/rtk-query/usage/manual-cache-updates#optimistic-updates
 *
 * useOptimisicDebounceMutation is extremely simple for PUT mutations
 * where the request is the full entity. Just call `set()` with the
 * full entity.
 * If the request holds only a partial representation, you must pass
 * an (optimistically) correct value, representing the updated entity
 * state as second parmeter. If backend validation fails, this value
 * will then be overwritten. Do not worry about validating this value
 * in the UI. It should be the result as the user input requests it.
 *
 * NOTE: If all text input components utilize debounced input components,
 * see {@link InputField, InputDebounced}, clients can actually set
 * the delay in the options to 0.
 *
 * @param initialRequest The value used to initialise the request send,
 *                       via the mutation. Also the error-reset value
 *                       passed to useDebounceMutation.
 * @param updateCache    A ThunkAction constructor that optimistically
 *                       patches the cache. You may use the `updateCache`
 *                       helper to dynamically construct it.
 * @param mutation       The RTK Query mutation, return of `useFooPutMutation()`
 * @param options        Options passed to {@link DebouncedMutationOptions}.
 *                       Note that the defaults are slightly changed:
 *                       1. There is an intentionally long delay.
 *                       2. resetOnError is set to false. This allows to use
 *                          the returned error to display validation errors
 *                          on input fields.
 * @returns `set(request, cacheValue)` — dispatches the optimistic cache update
 *          and schedules the mutation. `cacheValue` is required when the request
 *          is only a partial representation (PATCH) and optional when it is the
 *          full entity (PUT).
 */
export function useOptimisticDebouncedMutation<CacheValue, Req, Res>(
  initialRequest: NoInfer<Req>,
  updateCache: CacheUpdate<CacheValue>,
  mutation: RtkQueryMutation<Req, Res>,
  options?: Partial<DebouncedMutationOptions<Req, Res>>,
): [
  (request: Req, ...rest: CacheArg<Req, CacheValue>) => void,
  { error: unknown; isLoading: boolean },
] {
  const dispatch = useAppDispatch();

  // The local buffer is used to drive the debounce.
  const [buffer, setBuffer] = useState<Req>(initialRequest);
  const { error, isLoading } = useDebouncedMutation(
    buffer,
    setBuffer,
    mutation,
    initialRequest,
    { delay: 1000, resetOnError: false, ...options },
  );

  return [
    useCallback(
      (request: Req, ...rest: CacheArg<Req, CacheValue>) => {
        // The empty-tuple (no cacheValue) branch is only reachable when `Req` is
        // assignable to `CacheValue`, so falling back to `request` is sound.
        const cacheValue = (rest.length ? rest[0] : request) as CacheValue;
        // Immediate: patch the query cache for instant UI.
        dispatch(updateCache(cacheValue));
        // Debounced: set the buffer, which triggers the
        // mutation through useDebouncedMutation()
        setBuffer(request);
      },
      [dispatch, updateCache],
    ),
    { error, isLoading },
  ];
}
